import { FeatureEnv } from "./feature-env.model";

/**
 * Feature environments the user has visited, so a branch name is typed at most
 * once. Mirrors the storage conventions of src/app/v2/mocks/mock-scenario.ts:
 * a `dev.` key, and every access wrapped so private mode degrades to "no
 * recents" rather than breaking the dev panel.
 */
export const FEATURE_ENV_RECENTS_STORAGE_KEY = "dev.featureEnvRecents";

/** Per repo, not global — one busy repo must not evict the other. */
export const RECENTS_PER_REPO = 10;

export const RECENTS_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface RecentFeatureEnv extends FeatureEnv {
  readonly lastUsedAt: number;
}

/**
 * Returns recent feature environments, globally sorted by most recently used
 * first, with at most RECENTS_PER_REPO entries per repo.
 */
export function readRecentFeatureEnvs(now: number = Date.now()): RecentFeatureEnv[] {
  try {
    const raw = globalThis.localStorage?.getItem(FEATURE_ENV_RECENTS_STORAGE_KEY);
    return prune(parse(raw), now);
  } catch {
    return [];
  }
}

export function recordRecentFeatureEnv(env: FeatureEnv, now: number = Date.now()): void {
  try {
    const others = readRecentFeatureEnvs(now).filter(
      (recent) => !(recent.repo === env.repo && recent.branch === env.branch),
    );
    // Second prune is needed: if this is a new branch and the repo already has 10, appending brings it to 11.
    const next = prune([...others, { repo: env.repo, branch: env.branch, lastUsedAt: now }], now);
    globalThis.localStorage?.setItem(FEATURE_ENV_RECENTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable — the environment still applies, it just is not remembered.
  }
}

export function clearRecentFeatureEnvs(): void {
  try {
    globalThis.localStorage?.removeItem(FEATURE_ENV_RECENTS_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Branch names for one repo, most recently used first. */
export function recentBranchesForRepo(recents: readonly RecentFeatureEnv[], repo: string): string[] {
  return recents
    .filter((recent) => recent.repo === repo)
    .slice()
    .sort((left, right) => right.lastUsedAt - left.lastUsedAt)
    .map((recent) => recent.branch);
}

function parse(raw: string | null | undefined): RecentFeatureEnv[] {
  if (!raw) {
    return [];
  }
  const parsed: unknown = JSON.parse(raw);
  return Array.isArray(parsed) ? parsed.filter(isRecent) : [];
}

function isRecent(value: unknown): value is RecentFeatureEnv {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Partial<RecentFeatureEnv>;
  return (
    typeof candidate.repo === "string" &&
    typeof candidate.branch === "string" &&
    typeof candidate.lastUsedAt === "number"
  );
}

/**
 * Drops expired entries, caps each repo at RECENTS_PER_REPO newest, then
 * sorts globally by most recent first.
 */
function prune(recents: readonly RecentFeatureEnv[], now: number): RecentFeatureEnv[] {
  const fresh = recents.filter((recent) => now - recent.lastUsedAt < RECENTS_MAX_AGE_MS);
  const repos = new Set(fresh.map((recent) => recent.repo));

  const capped = Array.from(repos).flatMap((repo) =>
    fresh
      .filter((recent) => recent.repo === repo)
      .slice()
      .sort((left, right) => right.lastUsedAt - left.lastUsedAt)
      .slice(0, RECENTS_PER_REPO),
  );

  return [...capped].sort((left, right) => right.lastUsedAt - left.lastUsedAt);
}
